// Enhanced Food Acne Analysis Engine with Cuisine Support

export interface FoodScanAnalysis {
  foodName: string;
  ingredientsText?: string;
  cuisine?: string;
  acneImpactScore: number; // 1–100
  acneImpactCategory: 'low' | 'moderate' | 'high';
  explanation: string;
  triggers: string[];
  protectiveFactors: string[];
  tags: string[];
  suggestions: string[];
}

// Cuisine detection keywords - expanded to include uncommon dishes from all cuisines
const CUISINE_KEYWORDS: Record<string, string[]> = {
  indian: [
    'biryani', 'tikka', 'masala', 'samosa', 'dal', 'naan', 'curry', 'butter chicken', 'paneer', 'tandoori', 'pakora', 'dosa', 'idli',
    'sambar', 'rasam', 'sambar rice', 'rasam rice', 'sambar and rice', 'rasam and rice', 'upma', 'poha', 'dhokla', 'vada', 'uttapam',
    'pav bhaji', 'bhel puri', 'pani puri', 'golgappa', 'chaat', 'korma', 'rogan josh', 'palak paneer', 'aloo gobi', 'baingan bharta',
    'chana masala', 'rajma', 'dal makhani', 'dal tadka', 'sabzi', 'subzi', 'paratha', 'roti', 'chapati', 'poori', 'bhature',
    'kadhi', 'rasam', 'sambar', 'avial', 'thoran', 'kootu', 'poriyal', 'pachadi', 'raita', 'pickle', 'achar'
  ],
  italian: ['pasta', 'pizza', 'risotto', 'carbonara', 'lasagna', 'ravioli', 'gnocchi', 'bruschetta', 'tiramisu', 'gelato', 'fettuccine', 'spaghetti', 'penne', 'linguine', 'tagliatelle', 'cannelloni', 'tortellini', 'osso buco', 'minestrone', 'caprese'],
  mexican: ['taco', 'burrito', 'quesadilla', 'enchilada', 'nachos', 'guacamole', 'salsa', 'churro', 'tamale', 'pozole', 'mole', 'ceviche', 'elote', 'chiles rellenos', 'flautas', 'tostadas'],
  japanese: ['sushi', 'ramen', 'tempura', 'teriyaki', 'udon', 'soba', 'miso', 'sashimi', 'yakitori', 'tonkatsu', 'katsu', 'okonomiyaki', 'takoyaki', 'donburi', 'bento', 'onigiri', 'mochi'],
  korean: ['kimchi', 'bulgogi', 'bibimbap', 'tteokbokki', 'korean bbq', 'japchae', 'banchan', 'galbi', 'japchae', 'bossam', 'samgyeopsal', 'gimbap', 'mandu'],
  american_fast_food: ['burger', 'fries', 'nuggets', 'milkshake', 'hot dog', 'fried chicken', 'onion rings', 'chicken wings', 'buffalo wings'],
  middle_eastern: ['shawarma', 'falafel', 'hummus', 'kebab', 'pita', 'baba ganoush', 'tabbouleh', 'fattoush', 'mujaddara', 'mansaf', 'kibbeh', 'kofta', 'shish taouk'],
  thai: ['pad thai', 'tom yum', 'green curry', 'massaman', 'som tam', 'mango sticky rice', 'pad see ew', 'pad krapow', 'tom kha', 'larb', 'som tam', 'khao soi'],
  chinese: ['fried rice', 'kung pao', 'sweet and sour', 'dim sum', 'dumpling', 'wonton', 'lo mein', 'orange chicken', 'general tso', 'honey garlic', 'sticky chicken', 'mapo tofu', 'peking duck', 'char siu', 'hot pot', 'xiaolongbao', 'la zi ji', 'chongqing chicken', 'sichuan chicken', 'mala chicken', 'dry-fried chicken', 'spicy chicken', 'gong bao', 'kung pao chicken'],
  chinese_american: ['orange chicken', 'general tso', 'sweet and sour', 'honey garlic chicken', 'sticky chicken', 'glazed chicken', 'panda express'],
  mediterranean: ['greek salad', 'tzatziki', 'feta', 'olive', 'gyro', 'moussaka', 'spanakopita', 'dolmades', 'baklava'],
  vietnamese: ['pho', 'banh mi', 'spring roll', 'goi cuon', 'bun cha', 'com tam', 'banh xeo', 'ca kho to'],
  filipino: ['adobo', 'sinigang', 'lechon', 'pancit', 'lumpia', 'kare-kare', 'halo-halo'],
  ethiopian: ['injera', 'doro wat', 'misir wat', 'shiro', 'kitfo', 'tibs'],
  turkish: ['kebab', 'doner', 'lahmacun', 'pide', 'borek', 'baklava', 'lokum'],
  lebanese: ['shawarma', 'manoushe', 'fattoush', 'mujaddara', 'kibbeh', 'tabbouleh'],
  persian: ['kebab', 'ghormeh sabzi', 'fesenjan', 'tahdig', 'zereshk polo', 'ash reshteh'],
  indonesian: ['nasi goreng', 'rendang', 'gado gado', 'satay', 'gado-gado', 'nasi padang'],
  malaysian: ['nasi lemak', 'laksa', 'char kway teow', 'roti canai', 'rendang'],
  singaporean: ['hainanese chicken rice', 'laksa', 'char kway teow', 'chili crab'],
  jamaican: ['jerk chicken', 'ackee and saltfish', 'curry goat', 'jerk pork', 'rice and peas'],
  caribbean: ['jerk', 'curry', 'rice and peas', 'plantain', 'callaloo'],
  african: ['jollof rice', 'fufu', 'egusi', 'suya', 'bobotie'],
  brazilian: ['feijoada', 'pão de açúcar', 'coxinha', 'brigadeiro', 'moqueca'],
  peruvian: ['ceviche', 'lomo saltado', 'aji de gallina', 'anticuchos'],
  argentinian: ['asado', 'empanada', 'milanesa', 'choripan'],
  spanish: ['paella', 'tapas', 'gazpacho', 'tortilla española', 'churros'],
  french: ['coq au vin', 'ratatouille', 'bouillabaisse', 'cassoulet', 'quiche'],
  german: ['schnitzel', 'sauerkraut', 'bratwurst', 'pretzel', 'spätzle'],
  russian: ['borscht', 'pelmeni', 'beef stroganoff', 'shashlik', 'blini'],
  greek: ['moussaka', 'souvlaki', 'spanakopita', 'dolmades', 'baklava'],
  moroccan: ['tagine', 'couscous', 'harira', 'pastilla', 'bstilla'],
  egyptian: ['koshari', 'ful medames', 'molokhia', 'shawarma'],
  pakistani: ['biryani', 'karahi', 'nihari', 'haleem', 'chapli kebab'],
  bangladeshi: ['biryani', 'hilsa', 'panta bhat', 'fuchka'],
  sri_lankan: ['rice and curry', 'hoppers', 'kottu roti', 'string hoppers'],
  nepalese: ['momo', 'dal bhat', 'thukpa', 'sel roti'],
  burmese: ['mohinga', 'tea leaf salad', 'shan noodles', 'laphet'],
  cambodian: ['amok', 'bai sach chrouk', 'nom banh chok'],
  laotian: ['larb', 'tam mak hoong', 'khao niao'],
  mongolian: ['buuz', 'khuushuur', 'bansh'],
  // Additional cuisines for comprehensive coverage
  nigerian: ['jollof rice', 'egusi soup', 'suya', 'pounded yam', 'pepper soup', 'akara', 'moin moin', 'banga soup'],
  south_african: ['bobotie', 'bunny chow', 'boerewors', 'biltong', 'malva pudding', 'koeksisters'],
  kenyan: ['ugali', 'nyama choma', 'sukuma wiki', 'githeri', 'chapati', 'mandazi'],
  ghanaian: ['jollof rice', 'banku', 'fufu', 'waakye', 'kenkey', 'red red'],
  west_african: ['jollof', 'fufu', 'egusi', 'pepper soup', 'plantain'],
  east_african: ['ugali', 'nyama choma', 'sukuma wiki', 'injera'],
  taiwanese: ['beef noodle soup', 'bubble tea', 'oyster omelette', 'stinky tofu', 'gua bao', 'lu rou fan'],
  hong_kong: ['dim sum', 'char siu', 'wonton noodle', 'pineapple bun', 'egg tart', 'milk tea'],
  tibetan: ['momo', 'thukpa', 'tsampa', 'yak butter tea', 'thenthuk'],
  portuguese: ['bacalhau', 'pastel de nata', 'caldo verde', 'francesinha', 'piri piri chicken'],
  dutch: ['stamppot', 'bitterballen', 'haring', 'stroopwafel', 'poffertjes'],
  polish: ['pierogi', 'bigos', 'kielbasa', 'golabki', 'zapiekanka'],
  scandinavian: ['smørrebrød', 'gravlax', 'meatballs', 'lutefisk', 'köttbullar'],
  swedish: ['meatballs', 'gravlax', 'smörgåsbord', 'köttbullar', 'prinsesstårta'],
  norwegian: ['gravlax', 'lutefisk', 'fårikål', 'kjøttkaker', 'lefse'],
  danish: ['smørrebrød', 'frikadeller', 'æbleskiver', 'rødgrød'],
  finnish: ['karjalanpiirakka', 'kalakukko', 'lihapullat', 'mustikkapiirakka'],
  israeli: ['falafel', 'hummus', 'shakshuka', 'sabich', 'malabi', 'bourekas'],
  syrian: ['kibbeh', 'fattoush', 'muhammara', 'shawarma', 'baklava'],
  iraqi: ['masgouf', 'kubba', 'dolma', 'tashreeb', 'qoozi'],
  iranian: ['ghormeh sabzi', 'fesenjan', 'tahdig', 'zereshk polo', 'ash reshteh'],
  afghan: ['kabuli pulao', 'mantu', 'ashak', 'bolani', 'qorma'],
  colombian: ['bandeja paisa', 'arepa', 'ajiaco', 'empanada', 'sancocho'],
  venezuelan: ['arepa', 'pabellón criollo', 'cachapa', 'hallaca', 'tequeños'],
  chilean: ['empanada', 'pastel de choclo', 'cazuela', 'completo', 'choripan'],
  ecuadorian: ['ceviche', 'llapingachos', 'hornado', 'encocado', 'bolon'],
  bolivian: ['salteñas', 'llajwa', 'anticuchos', 'api', 'silpancho'],
  paraguayan: ['chipá', 'sopa paraguaya', 'mbeju', 'bori bori'],
  uruguayan: ['chivito', 'asado', 'milanesa', 'empanada', 'dulce de leche'],
  cuban: ['ropa vieja', 'mojito', 'arroz con pollo', 'picadillo', 'yuca con mojo'],
  dominican: ['mangú', 'sancocho', 'pollo guisado', 'tostones', 'habichuelas'],
  puerto_rican: ['mofongo', 'arroz con gandules', 'pasteles', 'lechón', 'tostones'],
  haitian: ['griot', 'diri ak djon djon', 'tasso', 'pikliz', 'soup joumou'],
  trinidadian: ['doubles', 'roti', 'pelau', 'callaloo', 'bake and shark'],
  guyanese: ['roti', 'curry', 'pepperpot', 'cook-up rice', 'metemgee'],
  australian: ['meat pie', 'pavlova', 'lamington', 'vegemite', 'barramundi'],
  new_zealand: ['hangi', 'pavlova', 'fish and chips', 'lamb', 'whitebait fritter'],
  polynesian: ['poi', 'kalua pig', 'poke', 'lomi lomi salmon', 'haupia'],
  hawaiian: ['poke', 'kalua pig', 'lomi lomi salmon', 'haupia', 'poi'],
  canadian: ['poutine', 'butter tart', 'nanaimo bar', 'tourtière', 'maple syrup'],
  scottish: ['haggis', 'neeps and tatties', 'cullen skink', 'shortbread', 'black pudding'],
  irish: ['irish stew', 'colcannon', 'boxty', 'soda bread', 'full irish breakfast'],
  welsh: ['welsh rarebit', 'cawl', 'bara brith', 'laverbread', 'welsh cakes'],
  ukrainian: ['borscht', 'varenyky', 'holubtsi', 'syrniki', 'kutia'],
  czech: ['svíčková', 'goulash', 'trdelník', 'knedlíky', 'smažený sýr'],
  hungarian: ['goulash', 'langos', 'chicken paprikash', 'kürtőskalács', 'túrós csusza'],
  romanian: ['sarmale', 'mămăligă', 'mititei', 'ciorbă', 'cozonac'],
  bulgarian: ['banitsa', 'shopska salad', 'kavarma', 'tarator', 'lyutenitsa'],
  serbian: ['ćevapi', 'pljeskavica', 'sarma', 'ajvar', 'burek'],
  croatian: ['ćevapi', 'sarma', 'pašticada', 'štrukli', 'fritule'],
  bosnian: ['ćevapi', 'burek', 'sarma', 'begova čorba', 'klepe'],
  albanian: ['byrek', 'tavë kosi', 'qofte', 'flija', 'baklava'],
  georgian: ['khachapuri', 'khinkali', 'lobio', 'mtsvadi', 'churchkhela'],
  armenian: ['dolma', 'khorovats', 'harissa', 'lahmajoun', 'pakhlava'],
  azerbaijani: ['plov', 'dolma', 'kebab', 'qutab', 'pakhlava'],
  kazakh: ['beshbarmak', 'plov', 'manty', 'baursak', 'kumys'],
  uzbek: ['plov', 'manty', 'lagman', 'samsa', 'shashlik'],
  tajik: ['plov', 'qurutob', 'manty', 'shashlik', 'osh'],
  kyrgyz: ['beshbarmak', 'plov', 'manty', 'lagman', 'kuurdak'],
};

// Ingredient tags for analysis - expanded patterns to catch more foods
const TAG_PATTERNS: Record<string, RegExp[]> = {
  sugar: [
    /\b(sugar|corn syrup|fructose|glucose|sucrose|honey|maple syrup|agave|molasses|cane sugar|sweet|sweetened)\b/i,
    /\b(candy|sweets|dessert|treat|frosting|icing|syrup)\b/i,
    /\b(soda|pop|cola|soft drink|energy drink|sports drink|juice|lemonade)\b/i,
    /\b(cookie|cake|donut|muffin|pastry|pie|brownie|cupcake|biscuit)\b/i,
    /\b(ice cream|gelato|frozen yogurt|milkshake|smoothie|frappe)\b/i,
    /\b(chocolate|fudge|caramel|toffee|marshmallow)\b/i,
  ],
  dairy: [
    /\b(milk|skim|whole milk|cream|whey|casein|lactose|dairy)\b/i,
    /\b(cheese|cheddar|mozzarella|parmesan|feta|gouda|swiss|brie|cream cheese)\b/i,
    /\b(butter|yogurt|greek yogurt|sour cream|buttermilk|heavy cream)\b/i,
    /\b(ice cream|gelato|frozen yogurt|milkshake|pudding|custard)\b/i,
    /\b(mac and cheese|alfredo|carbonara|lasagna|pizza)\b/i,
  ],
  refinedCarbs: [
    /\b(white flour|wheat flour|white rice|pasta|noodles|ramen|instant noodles)\b/i,
    /\b(white bread|bagel|cracker|pretzel|white pasta|biscuit)\b/i,
    /\b(pizza|burger|sandwich|wrap|tortilla|naan|pita)\b/i,
  ],
  stirFried: [
    /\b(fried rice|egg fried rice|chicken fried rice|shrimp fried rice|veg fried rice|vegetable fried rice)\b/i,
    /\b(fried noodles|chow mein|lo mein|yakisoba|stir fry|stir-fry|stir-fried)\b/i,
    /\b(stir-fried vegetables|stir-fried chicken|stir-fried beef|stir-fried pork)\b/i,
    /\b(pad thai|pad see ew|drunk noodles)\b/i,
  ],
  deepFried: [
    /\b(deep-fried|deep fried|tempura|fries|chips|crispy|battered|breaded)\b/i,
    /\b(fried chicken|fried wings|fried fish|fried shrimp|fried calamari|fried pork|chicken wings)\b/i,
    /\b(nuggets|onion rings|mozzarella sticks|fish and chips)\b/i,
    /\b(donut|fritter|beignet|churro|funnel cake)\b/i,
    /\b(orange chicken|general tso|sweet and sour|honey garlic|sticky chicken|glazed chicken)\b/i,
    /\b(samosa|pakora|spring roll|egg roll)\b/i,
  ],
  sweetSauce: [
    /\b(sweet sauce|sweet glaze|orange glaze|honey glaze|sticky sauce|teriyaki glaze)\b/i,
    /\b(orange chicken|general tso|sweet and sour|honey garlic|honey chicken|sticky wings)\b/i,
    /\b(glazed|syrup|sugar glaze|corn syrup|honey sauce)\b/i,
  ],
  processedFats: [
    /\b(shortening|hydrogenated|partially hydrogenated|palm oil|vegetable oil blend)\b/i,
    /\b(trans fat|margarine|processed oil|canola oil|soybean oil)\b/i,
  ],
  spicy: [
    /\b(spicy|chili|chilli|chile|hot sauce|jalapeño|habanero|cayenne)\b/i,
    /\b(curry|masala|sriracha|wasabi|pepper|peppery)\b/i,
  ],
  highGlycemic: [
    /\b(white bread|soda|pop|cola|soft drink|juice|instant noodles|white rice)\b/i,
    /\b(candy bar|chocolate bar|energy drink|sports drink|gatorade)\b/i,
    /\b(potato|fries|chips|mashed potato|baked potato)\b/i,
  ],
  antiInflammatory: [
    /\b(turmeric|ginger|garlic|green tea|matcha)\b/i,
    /\b(omega-3|fish oil|salmon|mackerel|sardines|tuna)\b/i,
  ],
  fiberRich: [
    /\b(oats|oatmeal|whole grain|whole wheat|brown rice|quinoa|barley)\b/i,
    /\b(lentils|beans|chickpeas|black beans|kidney beans|black beans)\b/i,
    /\b(vegetables|broccoli|spinach|kale|brussels sprouts|carrots)\b/i,
    /\b(berries|blueberry|strawberry|raspberry|blackberry|apple)\b/i,
    /\b(salad|greens|lettuce|arugula|watercress)\b/i,
  ],
  healthyFats: [
    /\b(olive oil|avocado|nuts|almonds|walnuts|cashews|pistachios)\b/i,
    /\b(seeds|chia|flax|hemp|pumpkin seeds|sunflower seeds)\b/i,
    /\b(omega-3|salmon|mackerel|tuna|sardines|herring)\b/i,
  ],
  fermented: [
    /\b(kimchi|yogurt|kefir|sauerkraut|miso|tempeh|kombucha)\b/i,
    /\b(pickled|fermented|probiotic)\b/i,
  ],
  dessert: [
    /\b(cake|cookie|biscuit|donut|muffin|pastry|pie|brownie|cupcake)\b/i,
    /\b(ice cream|gelato|frozen yogurt|pudding|custard|mousse)\b/i,
    /\b(chocolate|fudge|caramel|toffee|marshmallow|s'mores)\b/i,
    /\b(tiramisu|cheesecake|flan|creme brulee|tres leches)\b/i,
  ],
  processed: [
    /\b(chips|puffs|cheese puffs|crackers|pretzels|goldfish)\b/i,
    /\b(instant|processed|packaged|frozen meal|tv dinner)\b/i,
    /\b(nuggets|fingers|sticks|dippers|hot pocket|pizza roll)\b/i,
    /\b(cereal|granola bar|protein bar|energy bar)\b/i,
  ],
};

/**
 * Detects cuisine from food name and ingredients
 */
function detectCuisine(foodName: string, ingredientsText?: string): string | undefined {
  const combinedText = `${foodName} ${ingredientsText || ''}`.toLowerCase();
  
  for (const [cuisine, keywords] of Object.entries(CUISINE_KEYWORDS)) {
    for (const keyword of keywords) {
      if (combinedText.includes(keyword)) {
        return cuisine;
      }
    }
  }
  
  return undefined;
}

// Direct food name matching for common unhealthy foods
const UNHEALTHY_FOODS: Record<string, string[]> = {
  sugar: ['ice cream', 'milkshake', 'soda', 'coke', 'pepsi', 'candy', 'chocolate', 'cookie', 'cake', 'donut', 'brownie', 'cupcake', 'pie', 'pudding', 'fudge', 'caramel'],
  dairy: ['ice cream', 'milkshake', 'cheese', 'pizza', 'mac and cheese', 'alfredo', 'carbonara', 'lasagna', 'quesadilla', 'grilled cheese'],
  stirFried: ['fried rice', 'egg fried rice', 'chicken fried rice', 'shrimp fried rice', 'veg fried rice', 'fried noodles', 'chow mein', 'lo mein', 'yakisoba', 'stir fry', 'stir-fry', 'pad thai'],
  deepFried: ['fries', 'chips', 'fried chicken', 'fried wings', 'fried fish', 'fried shrimp', 'fried calamari', 'nuggets', 'onion rings', 'tempura', 'samosa', 'pakora', 'fritter', 'donut', 'orange chicken', 'general tso', 'sweet and sour', 'honey garlic chicken', 'sticky chicken', 'glazed chicken'],
  processed: ['chips', 'crackers', 'pretzels', 'nuggets', 'hot pocket', 'pizza roll', 'ramen', 'instant noodles'],
  highGlycemic: ['white bread', 'bagel', 'pasta', 'white rice', 'potato', 'fries', 'mashed potato'],
  sweetSauce: ['orange chicken', 'general tso', 'sweet and sour', 'honey garlic', 'honey chicken', 'sticky wings', 'glazed chicken', 'teriyaki chicken'],
};

/**
 * Extracts tags from food name and ingredients
 */
function extractTags(foodName: string, ingredientsText?: string): string[] {
  const combinedText = `${foodName} ${ingredientsText || ''}`.toLowerCase();
  const detectedTags: string[] = [];
  
  // First, check direct food name matches (more reliable)
  for (const [tag, foodList] of Object.entries(UNHEALTHY_FOODS)) {
    for (const food of foodList) {
      if (combinedText.includes(food)) {
        if (!detectedTags.includes(tag)) {
          detectedTags.push(tag);
        }
        // Also add related tags
        if (tag === 'sugar' && !detectedTags.includes('dessert')) {
          detectedTags.push('dessert');
        }
        if (tag === 'deepFried' && !detectedTags.includes('processed')) {
          detectedTags.push('processed');
        }
        break;
      }
    }
  }
  
  // Check for stir-fried dishes FIRST (before deep-fried) to avoid misclassification
  // Stir-fried dishes like "fried rice" should NOT be tagged as deep-fried
  if (TAG_PATTERNS.stirFried) {
    for (const pattern of TAG_PATTERNS.stirFried) {
      if (pattern.test(combinedText)) {
        if (!detectedTags.includes('stirFried')) {
          detectedTags.push('stirFried');
        }
        // Stir-fried dishes often have refined carbs
        if (!detectedTags.includes('refinedCarbs')) {
          detectedTags.push('refinedCarbs');
        }
        break;
      }
    }
  }
  
  // Then check regex patterns (but skip deepFried if stirFried is already detected)
  for (const [tag, patterns] of Object.entries(TAG_PATTERNS)) {
    // Skip if already detected
    if (detectedTags.includes(tag)) continue;
    
    // Don't tag as deepFried if it's already identified as stirFried
    if (tag === 'deepFried' && detectedTags.includes('stirFried')) {
      continue;
    }
    
    for (const pattern of patterns) {
      if (pattern.test(combinedText)) {
        if (!detectedTags.includes(tag)) {
          detectedTags.push(tag);
        }
        break;
      }
    }
  }
  
  // Special handling: If it's a sweet-glazed fried dish (like orange chicken), ensure both tags are present
  const sweetGlazedFriedPatterns = [
    /\b(orange chicken|general tso|sweet and sour chicken|honey garlic chicken|sticky chicken|glazed chicken)\b/i,
    /\b(fried.*(sweet|honey|orange|glaze|sticky)|(sweet|honey|orange|glaze|sticky).*fried)\b/i,
  ];
  
  for (const pattern of sweetGlazedFriedPatterns) {
    if (pattern.test(combinedText)) {
      if (!detectedTags.includes('deepFried')) {
        detectedTags.push('deepFried');
      }
      if (!detectedTags.includes('sweetSauce')) {
        detectedTags.push('sweetSauce');
      }
      if (!detectedTags.includes('sugar')) {
        detectedTags.push('sugar');
      }
      if (!detectedTags.includes('highGlycemic')) {
        detectedTags.push('highGlycemic');
      }
      if (!detectedTags.includes('processedFats')) {
        detectedTags.push('processedFats');
      }
      break;
    }
  }
  
  return detectedTags;
}

/**
 * Enhanced acne impact analysis with cuisine support
 */
export function analyzeFoodForAcne(input: {
  foodName: string;
  ingredientsText?: string;
}): FoodScanAnalysis {
  const { foodName, ingredientsText } = input;
  const combinedText = `${foodName} ${ingredientsText || ''}`.toLowerCase();
  
  // Detect cuisine
  const cuisine = detectCuisine(foodName, ingredientsText);
  
  // Extract tags
  const tags = extractTags(foodName, ingredientsText);
  
  // Start with a base score that will be adjusted
  // Use intelligent base score based on dish characteristics
  let score = 50;
  const triggers: string[] = [];
  const protectiveFactors: string[] = [];
  const suggestions: string[] = [];
  
  // === CHECK FOR SPECIFIC HEALTHY DISHES FROM ALL CUISINES (should get 1-25 score) ===
  const healthyDishPatterns = [
    // Indian healthy dishes
    /\b(upma|poha|dhokla|idli|dosa|uttapam|appam|pongal|khichdi|dal|sambar|rasam|kadhi)\b/i,
    // Korean healthy dishes
    /\b(kimchi|bibimbap|korean.*salad|banchan)\b/i,
    // Japanese healthy dishes
    /\b(miso soup|sashimi|sushi.*salmon|sushi.*tuna|edamame)\b/i,
    // Mediterranean healthy dishes
    /\b(greek salad|tzatziki|hummus|tabbouleh|fattoush)\b/i,
    // Vietnamese healthy dishes
    /\b(pho|goi cuon|bun cha|spring roll.*vegetable)\b/i,
    // Thai healthy dishes
    /\b(tom yum|som tam|green curry.*vegetable)\b/i,
    // Generic healthy dishes
    /\b(salad|soup.*vegetable|steamed.*vegetable|grilled.*fish|baked.*salmon)\b/i,
  ];
  
  let isHealthyDish = false;
  for (const pattern of healthyDishPatterns) {
    if (pattern.test(combinedText)) {
      isHealthyDish = true;
      // Exclude if it's deep-fried or has heavy unhealthy additions
      if (!tags.includes('deepFried') && !tags.includes('sugar') && !tags.includes('dessert')) {
        score = 20; // Start low for healthy dishes
        protectiveFactors.push('traditional healthy preparation');
        break;
      }
    }
  }
  
  // === CHECK FOR SPECIFIC UNHEALTHY DISHES (should get 76-100 score) ===
  const veryUnhealthyDishPatterns = [
    // Deep-fried dishes
    /\b(chicken 65|chicken65|gobi 65|paneer 65|fish 65|mushroom 65)\b/i,
    /\b(fried chicken|chicken wings|buffalo wings|onion rings|fries|french fries)\b/i,
    /\b(samosa|pakora|bhaji|vada|kachori|gulab jamun|jalebi|laddu|barfi)\b/i,
    // High sugar desserts
    /\b(ice cream|cake|cookie|brownie|donut|muffin|cupcake|pie|pudding|cheesecake)\b/i,
    // Processed snacks
    /\b(lays|potato chips|doritos|cheetos|pringles|fritos)\b/i,
    // Fast food
    /\b(burger|pizza|hot dog|nuggets|tenders|fried.*sandwich)\b/i,
  ];
  
  let isVeryUnhealthyDish = false;
  for (const pattern of veryUnhealthyDishPatterns) {
    if (pattern.test(combinedText)) {
      isVeryUnhealthyDish = true;
      score = 85; // Start high for very unhealthy dishes
      if (!triggers.includes('deep-fried') && combinedText.match(/65|fried|wings|rings|fries/i)) {
        triggers.push('deep-fried');
      }
      break;
    }
  }
  
  // === CHECK FOR VERY HEALTHY FOODS FIRST (fruits, vegetables, healthy snacks) ===
  // Use flexible patterns that match anywhere in the text
  const veryHealthyPatterns = [
    /\b(apple|banana|orange|grape|berry|strawberry|blueberry|raspberry|blackberry|kiwi|mango|pineapple|watermelon|peach|pear|cherry|plum)\b/i,
    /\b(salad|lettuce|spinach|kale|broccoli|carrot|celery|cucumber|tomato|pepper|zucchini|asparagus|cauliflower|brussels sprouts|green beans)\b/i,
    /\b(sweet potato|baked sweet potato)\b/i,
    /\b(avocado|nuts|almonds|walnuts|cashews|pistachios|pecans|hazelnuts)\b/i,
    /\b(salmon|mackerel|tuna|sardines|omega-3|herring)\b/i,
    /\b(quinoa|brown rice|oats|oatmeal|whole grain|barley|bulgur)\b/i,
    /\b(olive oil|avocado oil)\b/i,
  ];
  
  // Check for healthy snack patterns (e.g., "sweet potato chips in avocado oil")
  const healthySnackPatterns = [
    /sweet potato.*(baked|avocado oil|olive oil)/i,
    /(baked|air-popped).*(chips|snack)/i,
    /(roasted|baked).*(nuts|seeds|chickpeas)/i,
  ];
  
  let isVeryHealthy = false;
  for (const pattern of veryHealthyPatterns) {
    if (pattern.test(combinedText)) {
      isVeryHealthy = true;
      break;
    }
  }
  
  // Check for healthy snack patterns
  for (const pattern of healthySnackPatterns) {
    if (pattern.test(combinedText)) {
      isVeryHealthy = true;
      break;
    }
  }
  
  // EXCLUDE sweet-glazed fried dishes from "very healthy" even if they contain "orange" or other fruit words
  const isSweetGlazedFried = tags.includes('sweetSauce') || 
    combinedText.includes('orange chicken') || 
    combinedText.includes('general tso') || 
    combinedText.includes('sweet and sour') ||
    (tags.includes('deepFried') && (tags.includes('sugar') || tags.includes('sweetSauce')));
  
  // If it's a very healthy food AND doesn't have unhealthy additives, set low score immediately
  // BUT exclude sweet-glazed fried dishes
  if (isVeryHealthy && !tags.includes('sugar') && !tags.includes('dessert') && !tags.includes('processed') && !tags.includes('deepFried') && !isSweetGlazedFried) {
    if (!isHealthyDish) {
      score = 15; // Start very low for healthy foods
    } else {
      score = Math.min(score, 20); // Keep healthy dish score low
    }
    protectiveFactors.push('whole foods');
  }
  
  // If it's a healthy dish but not caught by veryHealthy patterns, ensure it stays low
  if (isHealthyDish && !isVeryHealthy && score > 25) {
    score = 20; // Force healthy dishes to low range
  }
  
  // === NEGATIVE FACTORS (increase score) - Apply to ALL foods ===
  
  // Count how many negative factors we have for cumulative impact
  let negativeFactorCount = 0;
  
  // High sugar (especially in desserts) - MAJOR IMPACT
  if (tags.includes('sugar') || tags.includes('dessert')) {
    const isDessert = tags.includes('dessert');
    const sugarImpact = isDessert ? 40 : 30;
    score += sugarImpact;
    negativeFactorCount++;
    triggers.push('high sugar');
    if (isDessert) {
      suggestions.push('Have this 1x/week max');
    }
  }
  
  // Dairy-heavy foods - MAJOR IMPACT
  if (tags.includes('dairy')) {
    const isDairyDessert = tags.includes('dairy') && tags.includes('dessert');
    const dairyImpact = isDairyDessert ? 35 : 25;
    score += dairyImpact;
    negativeFactorCount++;
    triggers.push('dairy');
    if (isDairyDessert) {
      suggestions.push('Consider dairy-free alternatives');
    }
  }
  
  // Deep-fried / fast food - MAJOR IMPACT
  if (tags.includes('deepFried') || (cuisine === 'american_fast_food' && tags.includes('processed'))) {
    // For very unhealthy dishes like chicken 65, ensure high score
    if (isVeryUnhealthyDish) {
      score = Math.max(score, 80); // Ensure very unhealthy dishes get high scores
    } else {
      score += 30;
    }
    negativeFactorCount++;
    triggers.push('deep-fried');
    if (cuisine === 'american_fast_food') {
      triggers.push('fast food');
    }
    suggestions.push('Opt for baked, air-fried, or grilled versions');
  }
  
  // Stir-fried dishes - MODERATE IMPACT (less than deep-fried)
  if (tags.includes('stirFried')) {
    score += 10; // Moderate penalty for oil, but not as high as deep-fried
    negativeFactorCount++;
    triggers.push('stir-fried');
    suggestions.push('Try a lighter stir-fry with less oil and more vegetables');
    
    // Extra penalty if combined with refined carbs (white rice/noodles + oil)
    if (tags.includes('refinedCarbs') || tags.includes('highGlycemic')) {
      score += 5;
      triggers.push('refined carbs with oil');
    }
  }
  
  // Sweet sauce/glaze - SIGNIFICANT IMPACT
  if (tags.includes('sweetSauce')) {
    score += 25;
    negativeFactorCount++;
    triggers.push('sweet glaze/sauce');
    if (!triggers.includes('high sugar')) {
      triggers.push('high sugar');
    }
  }
  
  // SPECIAL CASE: Deep-fried + Sweet sauce/glaze combo (like orange chicken, General Tso's)
  // This is a particularly bad combination for acne - extra penalty
  if (tags.includes('deepFried') && (tags.includes('sweetSauce') || tags.includes('sugar'))) {
    score += 20; // Extra penalty for oil + sugar together
    negativeFactorCount++;
    if (!triggers.includes('deep-fried')) {
      triggers.push('deep-fried');
    }
    if (!triggers.includes('high sugar')) {
      triggers.push('high sugar');
    }
    triggers.push('high oil + sugar combo');
    suggestions.push('Try a baked or air-fried version with a lighter sauce');
  }
  
  // High glycemic refined carbs - SIGNIFICANT IMPACT
  if (tags.includes('refinedCarbs') || tags.includes('highGlycemic')) {
    const carbImpact = tags.includes('refinedCarbs') && tags.includes('highGlycemic') ? 25 : 18;
    score += carbImpact;
    negativeFactorCount++;
    triggers.push('high glycemic carbs');
    if (!suggestions.includes('Opt for whole grain versions when possible')) {
      suggestions.push('Opt for whole grain versions when possible');
    }
  }
  
  // Processed snacks - SIGNIFICANT IMPACT (especially chips)
  if (tags.includes('processed')) {
    const isChips = combinedText.includes('chips') || combinedText.includes('lays');
    const processedImpact = isChips ? 25 : 18;
    score += processedImpact;
    negativeFactorCount++;
    if (!triggers.includes('ultra-processed')) {
      triggers.push('ultra-processed');
    }
  }
  
  // Processed fats
  if (tags.includes('processedFats')) {
    score += 18;
    negativeFactorCount++;
    triggers.push('processed fats');
  }
  
  // Specific VERY UNHEALTHY foods - HIGH IMPACT (direct name matching)
  const veryUnhealthyFoods = [
    { pattern: /\b(ice cream|milkshake|gelato)\b/i, impact: 20, name: 'ice cream' },
    { pattern: /\b(burger|fries|nuggets|fried chicken)\b/i, impact: 25, name: 'fast food' },
    { pattern: /\b(pizza|cheese pizza)\b/i, impact: 22, name: 'pizza' },
    { pattern: /\b(donut|frosting|glazed donut)\b/i, impact: 28, name: 'donut' },
    { pattern: /\b(lays|potato chips|chips)\b/i, impact: 25, name: 'chips' },
    { pattern: /\b(soda|pop|cola|soft drink)\b/i, impact: 30, name: 'soda' },
    { pattern: /\b(cake|cookie|brownie|cupcake)\b/i, impact: 25, name: 'dessert' },
    { pattern: /\b(candy|chocolate bar|sweets)\b/i, impact: 25, name: 'candy' },
    { pattern: /\b(ramen|instant noodles)\b/i, impact: 20, name: 'instant noodles' },
    // Sweet-glazed fried dishes (high oil + high sugar)
    { pattern: /\b(orange chicken|general tso|sweet and sour chicken|honey garlic chicken|sticky chicken|glazed chicken)\b/i, impact: 30, name: 'sweet-glazed fried chicken' },
  ];
  
  for (const food of veryUnhealthyFoods) {
    if (food.pattern.test(combinedText)) {
      score += food.impact;
      negativeFactorCount++;
      if (!triggers.includes(food.name)) {
        triggers.push(food.name);
      }
      break; // Only count once
    }
  }
  
  // Bonus penalty for multiple negative factors (synergistic effect)
  if (negativeFactorCount >= 3) {
    score += 15; // Multiple triggers compound the impact significantly
  } else if (negativeFactorCount >= 2) {
    score += 8;
  }
  
  // === POSITIVE FACTORS (decrease score) ===
  
  // Count positive factors for balanced scoring
  let positiveFactorCount = 0;
  
  // Only apply positive factors if we don't have major negative factors
  // This ensures unhealthy foods stay high, healthy foods stay low
  const hasMajorNegativeFactors = negativeFactorCount >= 2 || tags.includes('sugar') || tags.includes('dessert') || tags.includes('dairy') || tags.includes('processed');
  
  // Always apply positive factors for very healthy foods, or if no major negatives
  if (isVeryHealthy || !hasMajorNegativeFactors) {
    // Fiber-rich foods (not desserts)
    if (tags.includes('fiberRich') && !tags.includes('dessert')) {
      score -= 30;
      positiveFactorCount++;
      protectiveFactors.push('fiber');
      if (!suggestions.includes('Great source of fiber')) {
        suggestions.push('Great source of fiber');
      }
    }
    
    // Healthy fats / omega-3s
    if (tags.includes('healthyFats')) {
      const healthyFatImpact = combinedText.includes('omega-3') || combinedText.includes('salmon') ? 30 : 22;
      score -= healthyFatImpact;
      positiveFactorCount++;
      protectiveFactors.push('healthy fats');
    }
    
    // Fermented / probiotic foods
    if (tags.includes('fermented')) {
      score -= 22;
      positiveFactorCount++;
      protectiveFactors.push('probiotics');
      suggestions.push('Fermented foods support gut health');
    }
    
    // Anti-inflammatory ingredients
    if (tags.includes('antiInflammatory')) {
      score -= 22;
      positiveFactorCount++;
      protectiveFactors.push('anti-inflammatory');
    }
    
    // Explicitly healthy labels
    const healthyLabels = ['unsweetened', 'no added sugar', 'dairy-free', 'whole grain', 'organic', 'natural', 'baked', 'air-popped'];
    const hasHealthyLabel = healthyLabels.some(label => combinedText.includes(label));
    if (hasHealthyLabel) {
      score -= 20;
      positiveFactorCount++;
      if (combinedText.includes('unsweetened') || combinedText.includes('no added sugar')) {
        protectiveFactors.push('low sugar');
      }
      if (combinedText.includes('dairy-free')) {
        protectiveFactors.push('dairy-free');
      }
      if (combinedText.includes('baked') || combinedText.includes('air-popped')) {
        protectiveFactors.push('not fried');
      }
    }
    
    // Healthy snack patterns (sweet potato chips in avocado oil, etc.)
    if (combinedText.includes('sweet potato') && (combinedText.includes('baked') || combinedText.includes('avocado oil') || combinedText.includes('olive oil'))) {
      score -= 25;
      positiveFactorCount++;
      protectiveFactors.push('healthy preparation');
      protectiveFactors.push('whole food snack');
    }
    
    // Fruits and vegetables get strong positive impact
    if (combinedText.match(/\b(apple|banana|orange|grape|berry|strawberry|blueberry|raspberry|blackberry|kiwi|mango|pineapple|watermelon|peach|pear|cherry|plum)\b/i)) {
      score -= 20;
      positiveFactorCount++;
      protectiveFactors.push('fruit');
    }
    
    if (combinedText.match(/\b(salad|lettuce|spinach|kale|broccoli|carrot|celery|cucumber|tomato|pepper|zucchini|asparagus|cauliflower|brussels sprouts|green beans)\b/i)) {
      score -= 20;
      positiveFactorCount++;
      protectiveFactors.push('vegetables');
    }
    
    // Cuisine-specific adjustments for healthy options
    if (cuisine === 'korean' && tags.includes('fermented')) {
      // Korean fermented foods like kimchi are beneficial
      score -= 20;
      positiveFactorCount++;
    }
    
    if (cuisine === 'mediterranean' && tags.includes('healthyFats')) {
      // Mediterranean diet is generally skin-friendly
      score -= 20;
      positiveFactorCount++;
    }
    
    // Bonus for multiple positive factors
    if (positiveFactorCount >= 3) {
      score -= 15; // Multiple healthy factors compound benefits
    }
  }
  
  // Ensure score varies - add variation based on food name hash for consistency
  // This ensures same food always gets same score, but different foods get different scores
  let hash = 0;
  for (let i = 0; i < combinedText.length; i++) {
    const char = combinedText.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Add more variation based on score range
  let variation = 0;
  if (score <= 25) {
    // For healthy foods (1-25), add small variation (-3 to +3)
    variation = (Math.abs(hash) % 7) - 3;
  } else if (score >= 75) {
    // For very unhealthy foods (76-100), add small variation (-3 to +3)
    variation = (Math.abs(hash) % 7) - 3;
  } else {
    // For moderate foods (26-75), add larger variation (-5 to +5)
    variation = (Math.abs(hash) % 11) - 5;
  }
  score += variation;
  
  // Clamp score to appropriate ranges based on dish type
  if (isHealthyDish && !isVeryUnhealthyDish) {
    // Healthy dishes should be 1-25
    score = Math.max(1, Math.min(25, Math.round(score)));
  } else if (isVeryUnhealthyDish) {
    // Very unhealthy dishes should be 76-100
    score = Math.max(76, Math.min(100, Math.round(score)));
  } else {
    // Other dishes: clamp to 1-100
    score = Math.max(1, Math.min(100, Math.round(score)));
  }
  
  // Determine category with user-requested thresholds
  // Acne-friendly: 1-25, Moderate: 26-50, Bad: 51-75, Very bad: 76-100
  let acneImpactCategory: 'low' | 'moderate' | 'high';
  if (score <= 25) {
    acneImpactCategory = 'low'; // Acne-friendly
  } else if (score <= 50) {
    acneImpactCategory = 'moderate'; // Moderate
  } else if (score <= 75) {
    acneImpactCategory = 'high'; // Bad
  } else {
    acneImpactCategory = 'high'; // Very bad (76-100)
  }
  
  // Generate explanation
  let explanation = '';
  if (acneImpactCategory === 'high') {
    // Special case: Deep-fried + sweet sauce combo (like orange chicken)
    if (tags.includes('deepFried') && (tags.includes('sweetSauce') || tags.includes('sugar'))) {
      explanation = 'This dish is deep-fried and coated in a sugary glaze, which can spike blood sugar and increase inflammation, making it more likely to trigger acne. Try a baked or air-fried version with a lighter sauce instead.';
    } else if (tags.includes('deepFried')) {
      // Deep-fried (but not with sweet sauce)
      const triggerList = triggers.slice(0, 3).join(', ');
      explanation = `This looks like a ${triggerList} food, which can spike insulin and may worsen acne for some people.`;
      if (cuisine) {
        explanation += ` (${cuisine.replace('_', ' ')} cuisine)`;
      }
    } else if (tags.includes('stirFried')) {
      // Stir-fried dishes (like fried rice)
      explanation = 'This looks like a stir-fried dish made with added oil. Oily stir-fried foods with refined carbs can still impact blood sugar and skin, especially if eaten often. Try a lighter version with less oil, more vegetables, and whole grains.';
    } else {
      const triggerList = triggers.slice(0, 3).join(', ');
      explanation = `This looks like a ${triggerList} food, which can spike insulin and may worsen acne for some people.`;
      if (cuisine) {
        explanation += ` (${cuisine.replace('_', ' ')} cuisine)`;
      }
    }
  } else if (acneImpactCategory === 'moderate') {
    if (triggers.length > 0 && protectiveFactors.length > 0) {
      explanation = `This meal has some ${triggers[0]} but also ${protectiveFactors[0]}, so it's okay occasionally in moderation.`;
    } else if (triggers.length > 0) {
      explanation = `This food contains ${triggers[0]}, which may have a moderate impact on acne for some individuals.`;
    } else {
      explanation = 'This food has a moderate impact on acne.';
    }
  } else {
    if (protectiveFactors.length > 0) {
      explanation = `Mostly whole, unprocessed ingredients with ${protectiveFactors.join(' and ')}, plus low sugar — generally acne-friendly.`;
    } else {
      explanation = 'This food appears to have minimal acne-triggering ingredients.';
    }
  }
  
  // Add default suggestions if none exist
  if (suggestions.length === 0) {
    if (acneImpactCategory === 'high') {
      suggestions.push('Consider healthier alternatives');
      suggestions.push('Limit to occasional treats');
    } else if (acneImpactCategory === 'moderate') {
      suggestions.push('Enjoy in moderation');
    }
  }
  
  // Debug logging for food analysis
  console.log('DEBUG_FOOD_ANALYSIS', {
    foodName,
    tags,
    acneImpactScore: score,
    triggers,
    acneImpactCategory,
  });
  
  return {
    foodName,
    ingredientsText,
    cuisine,
    acneImpactScore: score,
    acneImpactCategory,
    explanation,
    triggers,
    protectiveFactors,
    tags,
    suggestions,
  };
}

